import { AssetAmount } from "@sundaeswap/asset";
import { UseQueryResult, useQueries, useQuery } from "@tanstack/react-query";
import { IAssetMetaData } from "../../types/Asset.types";
import { sortTasteTestsByCreatedDate } from "../../utils/taste-test.utils";
import { getTasteTestSdk } from "../client/tasteTestSdk";
import { Asset, Status, TasteTest, Transaction } from "../generated/taste-test.sdk";

export type TTasteTestWithDerivedValues = TasteTest & {
  currentGatheredBalance: { amount: AssetAmount<IAssetMetaData>; amountUsd: number };
  currentSuppliedBalance: { amount: AssetAmount<Asset>; amountUsd: number };
  targetPrice: AssetAmount;
  targetPriceUsd: number;
};

export const useTasteTests = (): UseQueryResult<TasteTest[]> => {
  return useQuery(
    ["taste-tests"],
    async () => {
      const { getTasteTests } = await getTasteTestSdk();
      const data = await getTasteTests();
      const sortedData = sortTasteTestsByCreatedDate(data?.tasteTests as TasteTest[]);
      return sortedData;
    },
    {
      refetchOnWindowFocus: false,
      refetchInterval: 1000 * 60 * 1, // 1 Minute
      cacheTime: 1000 * 60 * 1, // 1 Minute
      staleTime: 1000 * 60 * 1, // 1 Minute
    }
  );
};

export const useTasteTestsByStatus = (status: Status[]): UseQueryResult<TasteTest[]> => {
  return useQuery(
    ["taste-tests", status],
    async () => {
      const { getTasteTests } = await getTasteTestSdk();
      const data = await getTasteTests();
      const sortedData = sortTasteTestsByCreatedDate(data?.tasteTests as TasteTest[]);
      return (
        sortedData?.filter(({ status: tasteTestStatus }) => status.includes(tasteTestStatus)) ?? []
      );
    },
    {
      refetchOnWindowFocus: false,
      refetchInterval: 1000 * 60 * 1, // 1 Minute
      cacheTime: 1000 * 60 * 1, // 1 Minute
      staleTime: 1000 * 60 * 1, // 1 Minute
    }
  );
};

export const useTasteTestByPolicyId = (policyID: string): UseQueryResult<TasteTest> => {
  return useQuery(
    ["taste-test-by-policy-id", policyID],
    async () => {
      if (!policyID) return {};
      const { getTasteTestByPolicyId } = await getTasteTestSdk();
      const data = await getTasteTestByPolicyId({ policyID });
      return data?.tasteTest;
    },
    {
      refetchOnWindowFocus: false,
      refetchInterval: 1000 * 60 * 1, // 1 Minute
      cacheTime: 1000 * 60 * 1, // 1 Minute
      staleTime: 1000 * 60 * 1, // 1 Minute
    }
  );
};

export const useTasteTestBySlug = (slug: string): UseQueryResult<TasteTest> => {
  return useQuery(
    ["taste-test-by-slug", slug],
    async () => {
      if (!slug) return {};
      const { getTasteTestBySlug } = await getTasteTestSdk();
      const data = await getTasteTestBySlug({ slug });
      return data?.tasteTestBySlug;
    },
    {
      refetchOnWindowFocus: false,
      refetchInterval: 1000 * 60 * 1, // 1 Minute
      cacheTime: 1000 * 60 * 1, // 1 Minute
      staleTime: 1000 * 60 * 1, // 1 Minute
    }
  );
};

export const useTasteTestHistory = (
  owner: string,
  tasteTest?: string,
  refetchInterval?: number
): UseQueryResult<Transaction[]> => {
  return useQuery(
    ["taste-test-contributions", owner, refetchInterval, tasteTest],
    async () => {
      if (!owner) return [];
      const { getTasteTestHistory } = await getTasteTestSdk();
      const data = await getTasteTestHistory({ owner, tasteTest });
      return data?.history;
    },
    {
      enabled: Boolean(tasteTest),
      refetchOnWindowFocus: false,
      refetchInterval: refetchInterval || 1000 * 60 * 1, // 1 Minute
      cacheTime: 1000 * 60 * 1, // 1 Minute
      staleTime: 1000 * 60 * 1, // 1 Minute
    }
  );
};

export const useAllTasteTestHistory = (owner: string, tasteTests?: string[]) => {
  const queriesInfo = useQueries({
    queries: (tasteTests || []).map((tasteTest) => ({
      queryKey: ["taste-test-contributions", owner, tasteTest],
      queryFn: async () => {
        if (!owner) return [];
        const { getTasteTestHistory } = await getTasteTestSdk();
        const data = await getTasteTestHistory({ owner, tasteTest });
        return data?.history.map((history) => ({
          ...history,
          tasteTest,
        }));
      },
      enabled: Boolean(tasteTest),
      refetchOnWindowFocus: false,
      refetchInterval: 1000 * 60 * 1, // 1 Minute
      cacheTime: 1000 * 60 * 1, // 1 Minute
      staleTime: 1000 * 60 * 1, // 1 Minute
    })),
  });

  return {
    someAreRefetching: queriesInfo.some(({ isRefetching }) => isRefetching),
    someAreLoading: queriesInfo.some(({ isLoading }) => isLoading),
    allAreLoaded: queriesInfo.every(({ isLoading, isSuccess }) => !isLoading && isSuccess),
    queries: queriesInfo.map((result) => ({
      ...result,
      data: result.data as (Transaction & { tasteTest: string })[],
    })),
  };
};

export const useTasteTestTransactionHistory = (
  policyID: string,
  refetchInterval = 1000 * 5 // 5 seconds
): UseQueryResult<Transaction[]> => {
  return useQuery(
    ["taste-test-transaction-history", policyID],
    async () => {
      if (!policyID) return [];
      const { getTasteTestTransactions } = await getTasteTestSdk();
      const data = await getTasteTestTransactions({ policyID });
      return data?.tasteTest?.transactions ?? [];
    },
    {
      enabled: !!policyID,
      refetchOnWindowFocus: false,
      refetchInterval,
    }
  );
};
